import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Megaphone, X } from 'lucide-react';
import { NotificationContext } from '@/contexts/NotificationContext';
import { ONBOARDING_V2_DASHBOARD_PATH } from '@/components/onboarding-v2/onboardingV2Routes';
import { InAppNotificationDialog } from './InAppNotificationDialog';
import {
  InAppAnnouncement,
  InAppSeverity,
  SEVERITY_PRESENTATION,
  inAppAnnouncementOf,
  resolveInAppPlacement,
} from '@/lib/notifications/inAppNotification';

const SEVERITY_ICON: Record<InAppSeverity, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

export const OVERVIEW_PATH = '/home';

export function isOverviewRoute(pathname: string): boolean {
  return String(pathname || '').replace(/\/+$/, '') === OVERVIEW_PATH;
}

export function isTopBannerRoute(pathname: string): boolean {
  const path = String(pathname || '').replace(/\/+$/, '');
  return isOverviewRoute(path) || path === ONBOARDING_V2_DASHBOARD_PATH;
}

interface LiveAnnouncement {
  id: string;
  title: string;
  message: string;
  createdAt: string | null;
  announcement: InAppAnnouncement;
}

function useLiveAnnouncements(dismissed: Set<string>): LiveAnnouncement[] {
  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications;

  return useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    const out: LiveAnnouncement[] = [];
    for (const n of notifications) {
      const row = n as unknown as Record<string, unknown>;
      const id = String(row.id || '');
      if (!id || dismissed.has(id)) continue;
      if (row.isRead === true) continue;
      const announcement = inAppAnnouncementOf(n);
      if (!announcement) continue;
      out.push({
        id,
        title: String(row.title || ''),
        message: String(row.message || ''),
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
        announcement,
      });
    }
    return out;
  }, [notifications, dismissed]);
}

function useCountdown(seconds: number | null, onElapsed: () => void): number | null {
  const [remaining, setRemaining] = useState<number | null>(seconds);
  const onElapsedRef = useRef(onElapsed);
  useEffect(() => {
    onElapsedRef.current = onElapsed;
  }, [onElapsed]);

  useEffect(() => {
    if (seconds === null) {
      setRemaining(null);
      return;
    }
    setRemaining(seconds);
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(0, seconds - Math.floor((Date.now() - startedAt) / 1000));
      setRemaining(left);
      if (left <= 0) {
        window.clearInterval(tick);
        onElapsedRef.current();
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [seconds]);

  return remaining;
}

const AnnouncementPill: React.FC<{
  item: LiveAnnouncement;
  onDismiss: () => void;
  onOpen: () => void;
}> = ({ item, onDismiss, onOpen }) => {
  const severity = SEVERITY_PRESENTATION[item.announcement.severity];
  const Icon = SEVERITY_ICON[item.announcement.severity];
  useCountdown(item.announcement.autoDismissSeconds, onDismiss);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.95, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.8 }}
      className="pointer-events-auto w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-2 border-white bg-white shadow-lg ring-1 ring-black/5"
      data-testid="in-app-announcement-pill"
      data-severity={item.announcement.severity}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${severity.iconWrapClass}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          data-testid="in-app-announcement-pill-open"
        >
          <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
          {item.message ? (
            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{item.message}</p>
          ) : null}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          data-testid="in-app-announcement-pill-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

const AnnouncementBanner: React.FC<{
  item: LiveAnnouncement;
  onDismiss: () => void;
  onOpen: () => void;
}> = ({ item, onDismiss, onOpen }) => {
  const severity = SEVERITY_PRESENTATION[item.announcement.severity];
  const Icon = SEVERITY_ICON[item.announcement.severity];
  const remaining = useCountdown(item.announcement.autoDismissSeconds, onDismiss);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className={`flex items-center gap-3 border-b px-4 py-2.5 ${severity.chipClass}`}
      data-testid="in-app-announcement-banner"
      data-severity={item.announcement.severity}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 truncate text-left"
        data-testid="in-app-announcement-banner-open"
      >
        <span className="text-xs font-semibold">{item.title}</span>
        {item.message ? <span className="ml-2 text-xs opacity-80">{item.message}</span> : null}
      </button>
      {remaining !== null ? (
        <span
          className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium tabular-nums"
          data-testid="in-app-announcement-banner-countdown"
        >
          {remaining}s
        </span>
      ) : null}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 transition-colors hover:bg-white/60"
        data-testid="in-app-announcement-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
};

export const InAppAnnouncementHost: React.FC = () => {
  const location = useLocation();
  const notificationContext = useContext(NotificationContext);

  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const [opened, setOpened] = useState<LiveAnnouncement | null>(null);
  const live = useLiveAnnouncements(dismissed);
  const onOverview = isOverviewRoute(location.pathname);

  const pills: LiveAnnouncement[] = [];
  const banners: LiveAnnouncement[] = [];
  for (const item of live) {
    const placement = resolveInAppPlacement(item.announcement);
    if (placement.pill) pills.push(item);
    if (placement.banner && onOverview) banners.push(item);
  }

  const open = useCallback(
    (item: LiveAnnouncement) => {
      setOpened(item);
      dismiss(item.id);
      Promise.resolve(notificationContext?.markAsRead?.(item.id)).catch(() => {
      });
    },
    [dismiss, notificationContext]
  );

  return (
    <>
      {banners.length > 0 && (
        <div
          className="fixed left-0 right-0 z-40 top-[var(--app-header-height)]"
          data-testid="in-app-announcement-banner-host"
        >
          <AnimatePresence initial={false}>
            {banners.map((item) => (
              <AnnouncementBanner
                key={item.id}
                item={item}
                onDismiss={() => dismiss(item.id)}
                onOpen={() => open(item)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[90] flex flex-col items-end gap-2"
        data-testid="in-app-announcement-pill-host"
      >
        <AnimatePresence initial={false}>
          {pills.map((item) => (
            <AnnouncementPill
              key={item.id}
              item={item}
              onDismiss={() => dismiss(item.id)}
              onOpen={() => open(item)}
            />
          ))}
        </AnimatePresence>
      </div>

      <InAppNotificationDialog
        open={!!opened}
        onClose={() => setOpened(null)}
        title={opened?.title || ''}
        createdAt={opened?.createdAt}
        announcement={opened?.announcement ?? null}
      />
    </>
  );
};

export default InAppAnnouncementHost;
